// BaseWorkflowTemplateService.js
//
// Base-scoped workflow templates live in `_workflowTemplates/{templateKey}`.
// Used so system types (e.g. PROJECT) can own per-base playbooks without
// mutating the global `_nodetypes/{typeKey}` document.

import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  Timestamp,
  where,
} from 'firebase/firestore';
import { collections, db } from 'firebaseConfig';
import { withBaseKey } from 'utilities/basePersistence';
import { generateKey } from 'utilities/Keys';
import {
  normalizeMissionTemplate,
  normalizeMissionTemplateList,
} from 'missions/missionTemplateUtils';

const WORKFLOW_TEMPLATES_COLLECTION = collections.workflowTemplates || '_workflowTemplates';

function normalizeString(value) {
  return String(value || '').trim();
}

/**
 * @param {object|null|undefined} template
 * @returns {boolean}
 */
export function isBaseWorkflowTemplate(template) {
  if (!template || typeof template !== 'object') return false;
  if (template.isBaseWorkflow === true) return true;
  if (normalizeString(template.scope).toUpperCase() === 'BASE') return true;
  if (normalizeString(template.workflowSource).toUpperCase() === 'BASE') return true;
  return Boolean(normalizeString(template.baseKey));
}

/**
 * @param {object} data
 * @param {string} [docId]
 * @returns {object|null}
 */
export function normalizeBaseWorkflowTemplateRow(data = {}, docId = '') {
  const key = normalizeString(data?.key) || normalizeString(docId);
  if (!key) return null;

  const normalized = normalizeMissionTemplate({
    ...data,
    key,
  });

  if (!normalized.key) return null;
  if (!normalized.title && (!normalized.tasks || normalized.tasks.length === 0)) {
    return null;
  }

  return {
    ...normalized,
    id: key,
    key,
    baseKey: normalizeString(data?.baseKey) || null,
    nodeType: normalizeString(data?.nodeType || data?.typeKey),
    scope: 'BASE',
    isBaseWorkflow: true,
    workflowSource: 'BASE',
    canDelete: true,
  };
}

/**
 * Merge type-bag templates with base-scoped templates.
 * Same key → type/system wins.
 *
 * @param {object[]} typeTemplates
 * @param {object[]} baseTemplates
 * @returns {object[]}
 */
export function mergeWorkflowTemplates(typeTemplates = [], baseTemplates = []) {
  const byKey = new Map();

  (Array.isArray(typeTemplates) ? typeTemplates : []).forEach((template) => {
    const key = normalizeString(template?.key);
    if (!key) return;
    byKey.set(key, {
      ...template,
      key,
      isBaseWorkflow: false,
      scope: normalizeString(template?.scope) || 'TYPE',
    });
  });

  (Array.isArray(baseTemplates) ? baseTemplates : []).forEach((template) => {
    const key = normalizeString(template?.key);
    if (!key || byKey.has(key)) return;
    byKey.set(key, template);
  });

  return Array.from(byKey.values());
}

function buildBaseWorkflowTemplatesQuery({ baseKey, nodeType } = {}) {
  const cleanedBaseKey = normalizeString(baseKey);
  const cleanedNodeType = normalizeString(nodeType);
  if (!cleanedBaseKey || !cleanedNodeType) return null;

  return query(
    collection(db, WORKFLOW_TEMPLATES_COLLECTION),
    where('baseKey', '==', cleanedBaseKey),
    where('nodeType', '==', cleanedNodeType),
  );
}

/**
 * One-shot load of base-scoped workflow templates for a type.
 *
 * @param {{ baseKey?: string, nodeType?: string }} options
 * @returns {Promise<object[]>}
 */
export async function listBaseWorkflowTemplates({ baseKey, nodeType } = {}) {
  const templatesQuery = buildBaseWorkflowTemplatesQuery({ baseKey, nodeType });
  if (!templatesQuery) return [];

  const snapshot = await getDocs(templatesQuery);
  return snapshot.docs
    .map((docSnap) => normalizeBaseWorkflowTemplateRow(docSnap.data() || {}, docSnap.id))
    .filter(Boolean);
}

/**
 * Subscribe to base-scoped workflow templates for a type.
 *
 * @param {{ baseKey?: string, nodeType?: string }} options
 * @param {function(object[]): void} callback
 * @returns {function(): void}
 */
export function subscribeToBaseWorkflowTemplates({ baseKey, nodeType } = {}, callback) {
  const templatesQuery = buildBaseWorkflowTemplatesQuery({ baseKey, nodeType });

  if (!templatesQuery || typeof callback !== 'function') {
    if (typeof callback === 'function') callback([]);
    return () => {};
  }

  return onSnapshot(
    templatesQuery,
    (snapshot) => {
      const rows = snapshot.docs
        .map((docSnap) => normalizeBaseWorkflowTemplateRow(docSnap.data() || {}, docSnap.id))
        .filter(Boolean);
      callback(rows);
    },
    (error) => {
      console.error('[BaseWorkflowTemplateService] subscribe error:', error);
      callback([]);
    },
  );
}

/**
 * Create or replace one base-scoped workflow template.
 *
 * @param {object} params
 * @param {string} params.baseKey
 * @param {string} params.nodeType
 * @param {object} params.template
 * @returns {Promise<object>}
 */
export async function saveBaseWorkflowTemplate({ baseKey, nodeType, template = {} } = {}) {
  const cleanedBaseKey = normalizeString(baseKey);
  const cleanedNodeType = normalizeString(nodeType);
  if (!cleanedBaseKey || !cleanedNodeType) {
    throw new Error('baseKey and nodeType are required to save a workflow template.');
  }

  const templateKey = normalizeString(template?.key) || generateKey();
  const normalized = normalizeMissionTemplate({
    ...template,
    key: templateKey,
  });

  if (!normalized.title) {
    throw new Error('Playbook title is required.');
  }

  const now = Timestamp.now();
  const payload = withBaseKey({
    ...normalized,
    key: templateKey,
    nodeType: cleanedNodeType,
    typeKey: cleanedNodeType,
    scope: 'BASE',
    isBaseWorkflow: true,
    workflowSource: 'BASE',
    dateModified: now,
    dateCreated: template?.dateCreated || now,
  }, cleanedBaseKey);

  await setDoc(doc(db, WORKFLOW_TEMPLATES_COLLECTION, templateKey), payload, { merge: true });
  return normalizeBaseWorkflowTemplateRow(payload, templateKey);
}

/**
 * Delete one base-scoped workflow template.
 *
 * @param {string} templateKey
 * @returns {Promise<void>}
 */
export async function deleteBaseWorkflowTemplate(templateKey) {
  const cleanedKey = normalizeString(templateKey);
  if (!cleanedKey) return;
  await deleteDoc(doc(db, WORKFLOW_TEMPLATES_COLLECTION, cleanedKey));
}

/**
 * Sync a full template list for a base + type (upsert + delete removed).
 *
 * @param {object} params
 * @param {string} params.baseKey
 * @param {string} params.nodeType
 * @param {object[]} params.templates
 * @param {object[]} [params.previousTemplates]
 * @returns {Promise<object[]>}
 */
export async function syncBaseWorkflowTemplates({
  baseKey,
  nodeType,
  templates = [],
  previousTemplates = [],
} = {}) {
  const nextList = normalizeMissionTemplateList({ templates })
    .map((template) => normalizeMissionTemplate(template))
    .filter((template) => template.key && template.title);

  const nextKeys = new Set(nextList.map((template) => template.key));
  const previousKeys = (Array.isArray(previousTemplates) ? previousTemplates : [])
    .map((template) => normalizeString(template?.key))
    .filter(Boolean);

  await Promise.all(
    previousKeys
      .filter((key) => !nextKeys.has(key))
      .map((key) => deleteBaseWorkflowTemplate(key)),
  );

  const saved = [];
  for (const template of nextList) {
    // Sequential writes keep position ordering predictable under load.
    // eslint-disable-next-line no-await-in-loop
    saved.push(await saveBaseWorkflowTemplate({ baseKey, nodeType, template }));
  }

  return saved;
}
